<template>
    <div class="container-fluid px-0" v-show="show">
        <div v-if="author.banner_url">
            <img :src="author.banner_url + '?' + author.banner_hash" class="img-responsive img-cover">
        </div>
        <div v-else-if="banner">
            <img :src="banner" class="img-responsive img-cover">
        </div>
        <div class="container">
            <div class="row">
                <div class="col-xl-3 order-2 order-xl-1">
                    <div v-if="author" class="pb-4" itemscope itemtype="https://schema.org/Person">
                        <div class="in-banner mb-3" v-if="author.profile_url">
                            <img :src="author.profile_url + '?' + author.profile_hash" class="img-fluid img-thumbnail mx-auto d-block profile" />
                        </div>
                        <div>
                            <div class="d-flex justify-content-between">
                                <div class="mincho">
                                    <h2 class="inherit p-0 author" itemprop="givenName">
                                        <ruby>{{author.name}}<rt>{{author.kana}}</rt></ruby>
                                    </h2>
                                </div>
                                <div class="text-right pt-2">
                                    <button type="button" class="btn btn-outline-info btn-sm" v-if="!author.follower"
                                        :disabled="!canFollow" @click="followAuthor()">
                                        <i class="fas fa-heart"></i>
                                    </button>
                                    <button type="button" class="btn btn-outline-secondary btn-sm" v-else
                                        :disabled="!canFollow" @click="followAuthor()">
                                        <i class="fas fa-heart-broken"></i>
                                    </button>
                                </div>
                            </div>
                            <div class="ul py-2 overflow-hidden description" v-html="$filters.url(author.profile)" itemprop="description"></div>
                        </div>
                    </div>
                    <div class="mb-3" v-if="author.backlinks && author.backlinks.length">
                        <div class="d-flex justify-content-center small">
                            スポンサーリンク
                        </div>
                        <div class="d-flex justify-content-center mb-2 w-200 mx-auto" v-for="v in author.backlinks" :key="v.id">
                            <a :href="v.url" target="_blank">
                                <img :src="'/img/search/' + searchBanner(v.url)" :alt="v.link_name" class="img-thumbnail img-link" v-if="searchBanner(v.url)">
                                <img :src="v.picture.url +'?'+ v.picture.hash" :alt="v.link_name" class="img-thumbnail img-link" v-else-if="v.picture_id">
                                <div class="w-200 small">
                                    {{ v.link_name }}
                                </div>
                            </a>
                        </div>
                    </div>

                    <div class="mb-3" v-if="author.banner && author.banner.picture">
                        <div class="d-flex justify-content-center small">
                            本棚リンク用バナー
                        </div>
                        <div class="d-flex justify-content-center small">
                            <img :src="author.banner.picture.url +'?'+ author.banner.picture.hash" alt="リンクバナー" class="img-thumbnail img-link">
                        </div>
                    </div>

                    <div v-if="author.bluesky" class="mb-3">
                        <a :href="`https://bsky.app/profile/${author.bluesky}`" class="btn btn-primary btn-block btn-sm" target="_blank">
                            <img src="/img/bluesky-white.svg" class="sns-icon">
                            @{{author.bluesky}}
                        </a>
                    </div>
                    <div v-if="author.twitter" class="mb-3">
                        <a :href="`https://x.com/${author.twitter}`" class="btn btn-dark btn-block btn-sm" target="_blank">
                            <i class="fab fa-twitter"></i>
                            @{{author.twitter}}
                        </a>
                    </div>
                    <news></news>
                </div>
                <div class="col-xl-9 order-1 order-xl-2" v-if="genres">
                    <div class="pickup-bg" v-if="pickups.length">
                        <div class="row pb-2 d-flex justify-content-center">
                            <pickup-slider :pickups="pickups"></pickup-slider>
                        </div>
                    </div>

                    <div v-for="g in genres" :key="g.id" class="genre-block">
                        <h4 class="genre-name">
                            <span class="badge badge-light badge-hondana" v-if="g.is_hondana">
                                <i class="fas fa-book-open"></i>
                                展示棚
                            </span>
                            {{g.genre_name}}
                        </h4>
                        <div class="text-muted small pb-3">
                            {{g.description}}
                        </div>
                        <div v-if="g.bookList">
                            <hondana v-if="g.is_hondana"
                                :novels="g.bookList.data" >
                            </hondana>
                            <template v-else>
                                <div class="row" v-for="(bag, n) in genreNovels(g)" :key="'novel-row-' +n">
                                    <div :class="{
                                        'col-md-12': n === 0,
                                        'col-md-6': n !== 0,
                                    }" v-for="b in bag" :key="'novel-' +b.id">
                                        <slider :novels="b.seriesBooks"
                                            :caption="b.series.series_name"
                                            :link="'/author/series/' + b.series.id"
                                            childClass="mb-3 bg-base" v-if="b.seriesBooks && b.series.is_group && n === 0"></slider>
                                        <slider-mini :novels="b.seriesBooks"
                                            :caption="b.series.series_name"
                                            :link="'/author/series/' + b.series.id"
                                            childClass="mb-3 bg-base" v-else-if="b.seriesBooks && b.series.is_group"></slider-mini>
                                        <book-card :book="b" @updated="loadBooks()"
                                            :author="g.is_hondana"
                                            :directLink="g.is_hondana"
                                            v-else-if="n === 0"></book-card>
                                        <book-card-mini :book="b" @updated="loadBooks()"
                                            :author="g.is_hondana"
                                            :directLink="g.is_hondana"
                                            v-else-if="b.id"></book-card-mini>
                                    </div>
                                </div>
                            </template>
                            <div v-if="hasMoreBooks(g)" class="load-more d-flex justify-content-center align-content-center flex-wrap h-100 mb-3"
                                @click="loadMoreBooks(g)">
                                <div>
                                    もっと読み込む...
                                    <i class="fas fa-chevron-down"></i>
                                </div>
                            </div>
                        </div>
                        <article-pane
                            @loadArticle="loadArticle($event)"
                            :genre_id="g.id"
                            :articles="g.articleList"></article-pane>
                    </div>
                    <div class="nothing" v-if="!allNovels">
                        <h3>
                            まだ小説がありません
                        </h3>
                    </div>
                </div>
            </div>
        </div>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
    </div>
</template>

<style scoped lang="scss">
.author {
    font-size: 1.3rem;
}
.description {
    font-size: .8rem;
}
.pickup-bg {
    margin: -4rem 0 1rem;
}
.genre-block {
    padding-bottom: 2rem;
}
.genre-name {
    font-weight: bold;
}
.sns-icon {
    width: 0.74375rem;
    height: 0.74375rem;
}
.img-cover {
    width: 100%;
    max-height: 350px;
    object-fit: cover;
}
.img-link {
    width: calc(200px + 0.5rem + 2px);
    height: calc(40px + 0.5rem + 2px);
}
.w-200 {
    width: 200px;
}
.profile {
    width: 250px;
    height: 250px;
    object-fit: cover;
}
.in-banner {
    margin-top: -4rem;
}
.load-more {
    font-size: 1.2rem;
    font-weight: bold;
    cursor: pointer;
    &:hover {
        opacity: .5;
    }
}
.ul {
    border-bottom: 1px dotted #ddd;
}
.badge-hondana {
    font-size: 1.35rem;
}
@media (min-width: 1280px) {
    .container {
        max-width: 1480px;
    }
}
@media screen and (max-width: 770px) {
    .in-banner {
        margin-top: 1rem;
    }
    .pickup-bg {
        margin: 1rem 0;
    }
}
</style>

<script>
import bookCard from "./bookCard";
import bookCardMini from "./bookCardMini";
import articleCard from "./articleCard";
import page from "components/Page";
import pickupSlider from "./pickupSlider";
import slider from "pages/top/slider";
import sliderMini from "pages/top/sliderMini";
import articlePane from "./articlePane";
import hondana from "./hondana"
import news from "./news/news";
import searchServices from "@/searchServices";
export default {
    components: {
        bookCard,
        bookCardMini,
        articleCard,
        page,
        pickupSlider,
        slider,
        sliderMini,
        articlePane,
        hondana,
        news,
    },
    data() {
        return {
            books: [],
            pickups: [],
            author: {},
            genres: null,
            articles: null,
            complete: 0,    // 読み込み状況
            isLoading: false,
            banner: null,
            searches: searchServices,
        }
    },
    methods: {
        load() {
            this.loadAuthor();
            this.loadBooks();
            this.loadPickups();
        },
        loadAuthor() {
            axios.get("/author/get").then(res => {
                this.author = res.data;
                this.complete++;
            }).catch(err => { alert.err(err.response) });
        },
        loadBooks() {
            axios.get("/author/books/get").then(res => {
                this.genres = res.data;
                this.complete++;
                setTimeout(() => { this.$emit("updateHead"); });
                setTimeout(() => {$('[data-toggle="tooltip"]').tooltip();});
            }).catch(err => { alert.err(err.response) });
        },
        loadMoreBooks(genre) {
            if (this.isLoading) return;
            this.isLoading = true;
            axios.get("/author/books/more", {params: {
                offset: genre.bookList.data.length,
                genre_id: genre.id
            }}).then(res => {
                if (res.data.data) {
                    genre.bookList.data.push(...res.data.data)
                }
                this.isLoading = false;
            }).catch(err => { alert.err(err); this.isLoading = false; });
        },
        loadPickups() {
            axios.get("/author/books/pickups/get").then(res => {
                this.pickups = res.data;
                this.complete++;
            }).catch(err => { alert.err(err.response) });
        },
        loadArticle(params) {
            if (this.isLoading) return;
            this.isLoading = true;
            const genre_id = params.genre_id;
            const page = params.page;
            axios.get("/author/articles/genre", {params: {
                genre_id: genre_id,
                page: page,
            }}).then(res => {
                const genre = _.find(this.genres, {id: genre_id});
                genre.articleList.current_page = res.data.current_page;
                genre.articleList.last_page = res.data.last_page;
                for (const data of res.data.data) {
                    genre.articleList.data.push(data);
                }
                this.isLoading = false;
            }).catch(err => { alert.err(err); this.isLoading = false; });
        },
        followAuthor() {
            axios.post("/author/follow").then(res => {
                alert.msg(res.data);
                this.loadAuthor();
            }).catch(err => { alert.err(err.response) });
        },
        pickupUrl(p) {
            if (! p.is_long) {
                return "/author/page/" + p.id + "/section/" + p.sections[0].id;
            }
            return "/author/book/" + p.id;
        },
        genreNovels(genre) {
            if (!genre.bookList) return null;
            if (genre.is_hondana) {
                return genre.bookList.data;
            }
            const novels = genre?.bookList?.data || [];
            const genreNovels = [];
            if (novels.length) {
                genreNovels.push([novels[0]]);
                if (novels.length > 1) {
                    let bag = [];
                    for (let i = 1; i < novels.length; i++) {
                        bag.push(novels[i]);
                        if (i % 2 === 0) {
                            genreNovels.push(bag);
                            bag = [];
                        }
                    }
                    if (bag) {
                        genreNovels.push(bag);
                    }
                }
            }
            return genreNovels;
        },
        hasMoreBooks(genre) {
            if (!genre.bookList) return false;
            if (genre.bookList.data.length < genre.bookList.count) {
                return true;
            }
            return false;
        },
        loadBanner() {
            axios.get("/author/banner/get").then(res => {
                this.banner = res.data;
            }).catch(err => { alert.err(err.response) });
        },
        searchBanner(url) {
            for (const domain in this.searches) {
                const regex = new RegExp(domain, "i");
                if (url.match(regex)) {
                    return this.searches[domain].banner;
                }
            }
            return null;
        },
    },
    computed: {
        show() {
            return this.complete >= 3;
        },
        canFollow() {
            if (this.$store.state.user) {
                if (this.$store.state.user.id == this.author?.id) {
                    // 自分をフォローできない
                    return false;
                }
                return true;
            }
            return false;
        },
        description() {
            return this.author?.profile?.replace(/[\r\n]/ug, "").substr(0, 120) || "";
        },
        genresHasNovels() {
            const genres = [];
            for (const genre of this.genres) {
                if (this.books[genre.id] || (this.articles[genre.id] && this.articles[genre.id].total)) {
                    genres.push(genre);
                }
            }
            return genres;
        },
        allNovels() {
            let all = 0;
            for (const genre of this.genres) {
                if (genre.bookList) {
                    all += genre.bookList.data.length;
                }
                if (genre.articleList) {
                    all += genre.articleList.data.length;
                }
            }
            return all;
        }
    },
    mounted() {
        this.load();
        this.loadBanner();
        setTimeout(() => {
            this.useHead({
                title: this.$store.getters.bookshelfName + " | トップ",
                link: [ { rel: "canonical", href: location.origin + location.pathname, id: "canonical" } ],
                meta: [
                    { name: "keywords", content: this.author.keywords || "Web小説,オンライン小説,縦書き小説,小説投稿,小説置き場", id: "meta-keywords" },
                    { name: "description", content: this.description, id: "meta-description" }
                ]
            });
        }, 1000);
    }
}
</script>